/**
 * File utility functions for file type detection and handling
 */

/**
 * Detects if a Blob contains binary data by checking for null bytes
 * and non-text characters in the first chunk of the file.
 *
 * @param blob - The Blob to check
 * @param chunkSize - Number of bytes to sample (default: 8192)
 * @returns Promise<boolean> - true if the file appears to be binary
 *
 * @remarks
 * Detection strategy:
 * 1. Read the first chunk of the file (default 8KB)
 * 2. Check for null bytes (0x00) which are common in binary files
 * 3. Calculate ratio of non-printable characters
 * 4. If >30% non-printable or contains null bytes, classify as binary
 */
export async function isBinaryFile(
  blob: Blob,
  chunkSize: number = 8192
): Promise<boolean> {
  try {
    // Read the first chunk of the file
    const chunk = blob.slice(0, Math.min(chunkSize, blob.size));
    const arrayBuffer = await chunk.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    // Check for null bytes (strong indicator of binary data)
    for (let i = 0; i < uint8Array.length; i++) {
      if (uint8Array[i] === 0) {
        return true;
      }
    }

    // Count non-printable characters
    // Printable ASCII range: 32-126, plus common whitespace: 9 (tab), 10 (LF), 13 (CR)
    let nonPrintableCount = 0;
    for (let i = 0; i < uint8Array.length; i++) {
      const byte = uint8Array[i];
      const isPrintable =
        (byte >= 32 && byte <= 126) || byte === 9 || byte === 10 || byte === 13;

      if (!isPrintable) {
        nonPrintableCount++;
      }
    }

    // If more than 30% of characters are non-printable, consider it binary
    const nonPrintableRatio = nonPrintableCount / uint8Array.length;
    return nonPrintableRatio > 0.3;
  } catch (error) {
    console.error('Error detecting binary file:', error);
    // On error, default to treating as binary for safety
    return true;
  }
}

/**
 * Common binary file extensions
 */
const BINARY_EXTENSIONS = [
  // Executables and libraries
  '.exe',
  '.dll',
  '.so',
  '.dylib',
  '.bin',
  '.app',
  '.apk',
  '.deb',
  '.rpm',
  // Archives
  '.zip',
  '.tar',
  '.gz',
  '.bz2',
  '.7z',
  '.rar',
  '.dmg',
  '.iso',
  // Media (non-image)
  '.mp3',
  '.mp4',
  '.avi',
  '.mov',
  '.flv',
  '.wmv',
  '.wav',
  '.ogg',
  '.webm',
  // Documents
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  // Database
  '.db',
  '.sqlite',
  '.mdb',
  // Other binary formats
  '.wasm',
  '.pyc',
  '.class',
  '.o',
  '.a',
];

/**
 * Checks if a filename has a known binary extension
 *
 * @param filename - The filename to check
 * @returns boolean - true if the extension is a known binary format
 */
export function hasBinaryExtension(filename: string): boolean {
  const lowerFilename = filename.toLowerCase();
  return BINARY_EXTENSIONS.some(ext => lowerFilename.endsWith(ext));
}

/**
 * Combined check for binary files using both extension and content analysis
 *
 * @param blob - The Blob to check
 * @param filename - The filename (optional, for extension checking)
 * @returns Promise<boolean> - true if the file appears to be binary
 */
export async function detectBinaryFile(
  blob: Blob,
  filename?: string
): Promise<boolean> {
  // Fast path: check extension first
  if (filename && hasBinaryExtension(filename)) {
    return true;
  }

  // Slow path: analyze file content
  return await isBinaryFile(blob);
}
